/**
 * Zod schema for the overnight-update form (V2-E8.1, HAN-68). The end-of-shift
 * note a doctor records per patient — what happened overnight, what was done,
 * and (optionally) what the day team needs to know — which feeds the next
 * shift's morning handover. This is the single source of truth for the shape
 * and rules of an overnight-update payload, shared so the web form and the API
 * validate identically: the API's class-validator DTO mirrors these rules
 * field-for-field.
 *
 * Kept dependency-light: only `zod`. No imports from the API or drizzle.
 */
import { z } from "zod";

/** A required free-text field that must be non-empty after trimming. */
const requiredText = z.string().trim().min(1, "This field is required");

/**
 * Body of `PUT /sessions/:sessionId/patients/:patientId/overnight-update`.
 *
 * - `whatHappened` and `whatWasDone` are required — an overnight update with no
 *   account of the shift isn't an update.
 * - `dayTeamNotes` ("what the day team needs to know") is optional emphasis; a
 *   trimmed-empty value is treated as "no notes" and stored as null.
 */
export const overnightUpdateInputSchema = z.object({
  whatHappened: requiredText,
  whatWasDone: requiredText,
  dayTeamNotes: z.string().trim().optional(),
});

export type OvernightUpdateInput = z.infer<typeof overnightUpdateInputSchema>;
